import { ContentTypeFieldResponse, CPS_CONTENT_TYPE_FIELD_TYPE, CpsContentTypeFieldType } from '@cs-cms/types';
import { requiredValidator } from '../validators/FormValidator';
import { INT32_MAX, INT32_MIN, INT64_IEEEE754_MAX, INT64_IEEEE754_MIN } from './fieldMapperConstants';
import { FieldMapperContext, FieldMapperProps } from './fieldMapperTypes';

export function isFieldOptional(ctField: ContentTypeFieldResponse): boolean {
  return !ctField.required;
}

export function getRequiredValidator(
  ctField: ContentTypeFieldResponse
): typeof requiredValidator | undefined {
  return ctField.required ? requiredValidator : undefined;
}

export function getNumberFieldBounds(type: CpsContentTypeFieldType): { min?: number; max?: number } {
  if (type === CPS_CONTENT_TYPE_FIELD_TYPE.INT) {
    return { min: INT32_MIN, max: INT32_MAX };
  }
  if (type === CPS_CONTENT_TYPE_FIELD_TYPE.INT64) {
    return { min: INT64_IEEEE754_MIN, max: INT64_IEEEE754_MAX };
  }
  return {};
}

export function isSearchableExtDictionary(ctField: ContentTypeFieldResponse): boolean {
  return Boolean(ctField.searchable && ctField.usesExtDictionary);
}

export function shouldDisableFieldWhileLoading(ctField: ContentTypeFieldResponse, isLoading: boolean): boolean {
  if (!isLoading) {
    return false;
  }

  return !isSearchableExtDictionary(ctField);
}

export function isSearchableFormFillerExtDictionary(ctField: ContentTypeFieldResponse): boolean {
  return Boolean(ctField.searchable && ctField.formFiller && ctField.usesExtDictionary);
}

export function buildFieldMapperContext(props: FieldMapperProps): FieldMapperContext {
  const {
    ctField,
    fieldTranslation,
    choices,
    isLoading = false,
    onChange,
    onFilterChange,
    onSearch,
    onRefresh,
    showSearchButton = false,
    isEnabled,
    getFieldDisabled,
    renderFieldWrapper
  } = props;

  const fieldLabel = fieldTranslation ?? ctField.name;

  const handleChange = (event: any): void => {
    const value = event.value !== undefined ? event.value : event.target?.value;
    onChange?.(value);
  };

  const defaultFieldDisabled =
    ctField.readOnly || shouldDisableFieldWhileLoading(ctField, isLoading) || !isEnabled;
  const fieldDisabled = getFieldDisabled?.({ ctField, isLoading, isEnabled }) ?? defaultFieldDisabled;

  return {
    ctField,
    fieldLabel,
    choices,
    isLoading,
    fieldDisabled,
    handleChange,
    onChange,
    onFilterChange,
    onSearch,
    onRefresh,
    showSearchButton,
    renderFieldWrapper
  };
}
